import { memo, useCallback, useRef, useState } from 'react';
import { CheckIcon } from './icons';
import { smsConsentDisclaimer } from '../utils/content';

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;

/**
 * ContactForm — the "Get in Touch" lead form used in the Let's Connect section
 * and inside the pop-up modal. Submission is owned by the platform via
 * `onSubmit`; on success the form is replaced with a thank-you state.
 * `idPrefix` keeps input ids unique when the form renders twice on a page.
 */
export const ContactForm = memo(({ name, firstName, onSubmit, idPrefix = 'cf360' }) => {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
    ok_to_text: false
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const firstRef = useRef(null);
  const lastRef = useRef(null);
  const emailRef = useRef(null);

  const update = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();
      const nextErrors = {};
      if (!form.first_name.trim()) nextErrors.first_name = 'Please enter your first name.';
      if (!form.last_name.trim()) nextErrors.last_name = 'Please enter your last name.';
      if (!form.email.trim() || !EMAIL_REGEX.test(form.email)) nextErrors.email = 'Please enter a valid email address.';

      if (Object.keys(nextErrors).length) {
        setErrors(nextErrors);
        const focus = nextErrors.first_name ? firstRef : nextErrors.last_name ? lastRef : emailRef;
        focus.current?.focus();
        return;
      }

      setSubmitting(true);
      try {
        await onSubmit({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          message: form.message.trim(),
          text_permission: form.ok_to_text
        });
        setSubmitted(true);
      } catch {
        setSubmitting(false);
      }
    },
    [form, onSubmit]
  );

  const formNote = smsConsentDisclaimer(name || firstName);

  if (submitted) {
    return (
      <div className="contact-form">
        <div className="form-success">
          <span className="success-mark">
            <CheckIcon />
          </span>
          <h3>Thank you!</h3>
          <p>{firstName ? `${firstName} will be in touch shortly.` : 'You’ll hear back shortly.'}</p>
        </div>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      <h3 className="form-title">Get in Touch</h3>
      <div className="field-row">
        <div className="cw-field">
          <label htmlFor={`${idPrefix}-first`}>First Name *</label>
          <input
            id={`${idPrefix}-first`}
            ref={firstRef}
            name="first_name"
            type="text"
            autoComplete="given-name"
            maxLength={30}
            value={form.first_name}
            onChange={e => update('first_name', e.target.value)}
            className={errors.first_name ? 'invalid' : undefined}
            aria-invalid={errors.first_name ? 'true' : 'false'}
            aria-describedby={errors.first_name ? `${idPrefix}-first-error` : undefined}
          />
          {errors.first_name && (
            <p className="field-error" id={`${idPrefix}-first-error`}>
              {errors.first_name}
            </p>
          )}
        </div>
        <div className="cw-field">
          <label htmlFor={`${idPrefix}-last`}>Last Name *</label>
          <input
            id={`${idPrefix}-last`}
            ref={lastRef}
            name="last_name"
            type="text"
            autoComplete="family-name"
            maxLength={30}
            value={form.last_name}
            onChange={e => update('last_name', e.target.value)}
            className={errors.last_name ? 'invalid' : undefined}
            aria-invalid={errors.last_name ? 'true' : 'false'}
            aria-describedby={errors.last_name ? `${idPrefix}-last-error` : undefined}
          />
          {errors.last_name && (
            <p className="field-error" id={`${idPrefix}-last-error`}>
              {errors.last_name}
            </p>
          )}
        </div>
      </div>

      <div className="cw-field">
        <label htmlFor={`${idPrefix}-email`}>Email *</label>
        <input
          id={`${idPrefix}-email`}
          ref={emailRef}
          name="email"
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={e => update('email', e.target.value)}
          className={errors.email ? 'invalid' : undefined}
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby={errors.email ? `${idPrefix}-email-error` : undefined}
        />
        {errors.email && (
          <p className="field-error" id={`${idPrefix}-email-error`}>
            {errors.email}
          </p>
        )}
      </div>

      <div className="cw-field">
        <label htmlFor={`${idPrefix}-phone`}>Phone</label>
        <input
          id={`${idPrefix}-phone`}
          name="phone"
          type="tel"
          autoComplete="tel"
          value={form.phone}
          onChange={e => update('phone', e.target.value)}
        />
      </div>

      <div className="cw-field">
        <label htmlFor={`${idPrefix}-message`}>Message</label>
        <textarea
          id={`${idPrefix}-message`}
          name="message"
          rows={3}
          value={form.message}
          onChange={e => update('message', e.target.value)}
        />
      </div>

      <label className="consent">
        <input
          type="checkbox"
          name="ok_to_text"
          checked={form.ok_to_text}
          onChange={e => update('ok_to_text', e.target.checked)}
        />
        <span>It’s OK to text me at the number provided</span>
      </label>

      <button className="btn btn-primary btn-block" type="submit" disabled={submitting}>
        {submitting ? 'Sending…' : 'Submit'}
      </button>
      <p className="form-note">{formNote}</p>
    </form>
  );
});
